/**
 * Loads the Analytics country payload (GET /api/v1/analytics/countries) once,
 * and again whenever the main snapshot is refreshed (`version` changes).
 */

import { useEffect, useState } from 'react';
import { BACKEND_URL } from '@/config';
import type { AnalyticsCountryPayload } from '@/utils/analyticsCountryFilter';

export function useAnalyticsCountries(version: string | null | undefined) {
  const [payload, setPayload] = useState<AnalyticsCountryPayload | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!version) return;
    let cancelled = false;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/v1/analytics/countries`)
      .then(async r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(body => {
        if (cancelled) return;
        if (body && body.byCountry) {
          setPayload(body as AnalyticsCountryPayload);
          setMessage(body.complete ? null : 'Country data is still rebuilding — it becomes available after the next full analytics resync.');
        } else {
          setPayload(null);
          setMessage(body?.message || 'Country data is not available yet.');
        }
      })
      .catch(err => {
        if (!cancelled) setMessage(`Country data failed to load: ${err instanceof Error ? err.message : err}`);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [version]);

  return { payload, message, loading, ready: !!payload?.complete };
}
